
export const merchTweenEaseOut = (t: number): number => {
  const x = t <= 0 ? 0 : t >= 1 ? 1 : t
  return 1 - Math.pow(1 - x, 3)
}

export interface MerchTweenHandle {
  readonly values: number[]
  readonly finished: boolean
  stop: (snap: boolean) => void
}

export const startMerchTween = (
  from: number[],
  to: number[],
  durationMs: number,
  onFrame: (values: number[], done: boolean) => void
): MerchTweenHandle => {
  const values = from.slice()
  const raf = globalThis.requestAnimationFrame
  const caf = globalThis.cancelAnimationFrame
  let frame: number | null = null
  let finished = false

  const settle = (): void => {
    for (let i = 0; i < to.length; i++) values[i] = to[i]
    finished = true
    onFrame(values, true)
  }

  const handle: MerchTweenHandle = {
    values,
    get finished () { return finished },
    stop: (snap: boolean) => {
      if (frame !== null && typeof caf === 'function') caf(frame)
      frame = null
      if (finished) return
      if (snap) settle()
      else finished = true
    }
  }

  if (from.length === 0 || to.length !== from.length || !(durationMs > 0) || typeof raf !== 'function') {
    settle()
    return handle
  }

  let startedAt: number | null = null
  const step = (now: number): void => {
    frame = null
    if (finished) return
    if (startedAt === null) startedAt = now
    const t = (now - startedAt) / durationMs
    if (t >= 1) {
      settle()
      return
    }
    const k = merchTweenEaseOut(t)
    for (let i = 0; i < to.length; i++) values[i] = from[i] + (to[i] - from[i]) * k
    onFrame(values, false)
    frame = raf(step)
  }
  frame = raf(step)
  return handle
}

export const merchTweenMoney = (cents: number): string => (cents / 100).toFixed(2)

export interface MerchPctFormat {
  value: number
  decimals: number
  suffix: string
}

const MERCH_PCT_RE = /^([+-]?)(\d+)(?:\.(\d+))?(%?)$/

export const parseMerchPctLabel = (label: string | null | undefined): MerchPctFormat | null => {
  if (typeof label !== 'string') return null
  const m = MERCH_PCT_RE.exec(label)
  if (m === null) return null
  const frac = m[3] ?? ''
  const magnitude = Number(`${m[2]}.${frac === '' ? '0' : frac}`)
  if (!Number.isFinite(magnitude)) return null
  return {
    value: m[1] === '-' ? -magnitude : magnitude,
    decimals: frac.length,
    suffix: m[4]
  }
}

export const formatMerchTweenPct = (
  value: number,
  fmt: MerchPctFormat,
  maxLen: number,
  fallback: string
): string => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : ''
  const build = (decimals: number): string => `${sign}${Math.abs(value).toFixed(decimals)}${fmt.suffix}`
  const full = build(fmt.decimals)
  if (full.length <= maxLen) return full
  const bare = build(0)
  return bare.length <= maxLen ? bare : fallback
}
